<template>
  <div class="calendar-events" :class="{ 'calendar-events--vertical': vertical }">
    <!-- Left: Mini Calendar -->
    <div class="calendar-events__calendar">
      <!-- Navigation -->
      <div class="calendar-events__nav">
        <button class="calendar-events__today-btn" @click="goToToday">Today</button>
        <span class="calendar-events__nav-label">{{ monthYearLabel }}</span>
        <div class="calendar-events__nav-arrows">
          <button class="calendar-events__nav-btn" @click="prevMonth" aria-label="Previous month">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 12L6 8L10 4"
                stroke="#191919"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button class="calendar-events__nav-btn" @click="nextMonth" aria-label="Next month">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="#191919"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Day-of-week headers -->
      <div class="calendar-events__day-headers">
        <div v-for="day in dayHeaders" :key="day" class="calendar-events__day-header">{{ day }}</div>
      </div>

      <!-- Date grid -->
      <div class="calendar-events__grid">
        <div v-for="(week, wi) in calendarWeeks" :key="wi" class="calendar-events__week">
          <button
            v-for="(cell, ci) in week"
            :key="ci"
            class="calendar-events__cell"
            :class="{
              'calendar-events__cell--other-month': !cell.currentMonth,
              'calendar-events__cell--selected': isSelected(cell),
              'calendar-events__cell--today': isToday(cell)
            }"
            @click="selectDate(cell)"
          >
            <span class="calendar-events__cell-number">{{ cell.day }}</span>
            <span
              v-if="cell.currentMonth && getDotColor(cell)"
              class="calendar-events__dot"
              :style="{ backgroundColor: getDotColor(cell) }"
            ></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Right: Event Panel -->
    <div class="calendar-events__panel">
      <!-- Selected date events -->
      <div v-if="selectedDateEvents.length" class="calendar-events__section">
        <div class="calendar-events__section-heading">{{ selectedDateLabel }}</div>
        <div v-for="event in selectedDateEvents" :key="event.id" class="calendar-events__event-card">
          <div class="calendar-events__accent-bar" :style="{ backgroundColor: getEventColor(event) }"></div>
          <div class="calendar-events__event-body">
            <span class="calendar-events__event-text">{{ event.text }}</span>
            <slot name="event-action" :event="event"></slot>
          </div>
        </div>
      </div>

      <div v-else class="calendar-events__section">
        <div class="calendar-events__section-heading">{{ selectedDateLabel }}</div>
        <div class="calendar-events__empty">No events on this date.</div>
      </div>

      <!-- Coming up next -->
      <div v-if="upcomingEvents.length" class="calendar-events__section">
        <div class="calendar-events__section-heading">Coming up next</div>
        <div v-for="event in upcomingEvents" :key="event.id" class="calendar-events__upcoming-row">
          <div class="calendar-events__date-badge" :style="{ backgroundColor: getBadgeBackground(event) }">
            <span class="calendar-events__date-badge-month">{{ badgeMonth(event.date) }}</span>
            <span class="calendar-events__date-badge-day">{{ badgeDay(event.date) }}</span>
          </div>
          <div class="calendar-events__event-card calendar-events__event-card--flex">
            <div class="calendar-events__accent-bar" :style="{ backgroundColor: getEventColor(event) }"></div>
            <div class="calendar-events__event-body">
              <span class="calendar-events__event-text">{{ event.text }}</span>
              <slot name="event-action" :event="event"></slot>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="legend.length" class="calendar-events__legend">
        <v-tooltip v-if="legendTooltip.length" location="bottom" color="#484848" max-width="280px">
          <template #activator="{ props }">
            <button v-bind="props" class="calendar-events__legend-info-btn" aria-label="Legend info">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#747474" stroke-width="1.5" />
                <text
                  x="8"
                  y="12"
                  text-anchor="middle"
                  font-size="11"
                  font-weight="bold"
                  font-family="Arial, sans-serif"
                  fill="#747474"
                >
                  i
                </text>
              </svg>
            </button>
          </template>
          <div style="font-size: 14px; color: white">
            <div v-for="(tip, i) in legendTooltip" :key="i" :class="{ 'mt-3': i > 0 }">
              <span style="font-weight: bold">{{ tip.label }}</span>
              {{ tip.description }}
            </div>
          </div>
        </v-tooltip>
        <span class="calendar-events__legend-prefix">Legend:</span>
        <div v-for="(item, i) in legend" :key="i" class="calendar-events__legend-item">
          <span class="calendar-events__legend-dot" :style="{ backgroundColor: item.color }"></span>
          <span class="calendar-events__legend-label">{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { parseLocalDate } from '@/utils/dateUtils';

export default defineComponent({
  name: 'CalendarEvents',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    colorMap: {
      type: Object,
      default: () => ({})
    },
    defaultColor: {
      type: String,
      default: '#FFC627'
    },
    legend: {
      type: Array,
      default: () => []
    },
    legendTooltip: {
      type: Array,
      default: () => []
    },
    vertical: {
      type: Boolean,
      default: false
    }
  },
  emits: ['prev-month', 'next-month', 'today'],
  setup(props, { emit }) {
    const today = new Date();
    const currentMonth = ref(today.getMonth());
    const currentYear = ref(today.getFullYear());
    const selectedDate = ref(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

    const dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    const MONTH_NAMES = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    const monthYearLabel = computed(() => `${MONTH_NAMES[currentMonth.value]} ${currentYear.value}`);

    const calendarWeeks = computed(() => {
      const year = currentYear.value;
      const month = currentMonth.value;
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const daysInPrevMonth = new Date(year, month, 0).getDate();

      const weeks = [];
      let week = [];

      // Previous month trailing days
      for (let i = firstDay - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        week.push({ day, month: prevMonth, year: prevYear, currentMonth: false });
      }

      // Current month days
      for (let d = 1; d <= daysInMonth; d++) {
        week.push({ day: d, month, year, currentMonth: true });
        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
      }

      // Next month leading days
      if (week.length > 0) {
        let nextDay = 1;
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextYear = month === 11 ? year + 1 : year;
        while (week.length < 7) {
          week.push({ day: nextDay++, month: nextMonth, year: nextYear, currentMonth: false });
        }
        weeks.push(week);
      }

      return weeks;
    });

    // Build a Set of "YYYY-MM-DD" → color for dot lookup
    const eventDateMap = computed(() => {
      const map = {};
      props.events.forEach((ev) => {
        const key = ev.date;
        if (!map[key]) {
          map[key] = getColorForType(ev.type);
        }
      });
      return map;
    });

    function getColorForType(type) {
      return props.colorMap[type] || props.defaultColor;
    }

    function cellToDateStr(cell) {
      const m = String(cell.month + 1).padStart(2, '0');
      const d = String(cell.day).padStart(2, '0');
      return `${cell.year}-${m}-${d}`;
    }

    function getDotColor(cell) {
      return eventDateMap.value[cellToDateStr(cell)] || null;
    }

    function isSelected(cell) {
      return (
        cell.day === selectedDate.value.getDate() &&
        cell.month === selectedDate.value.getMonth() &&
        cell.year === selectedDate.value.getFullYear()
      );
    }

    function isToday(cell) {
      return (
        cell.currentMonth &&
        cell.day === today.getDate() &&
        cell.month === today.getMonth() &&
        cell.year === today.getFullYear()
      );
    }

    function selectDate(cell) {
      selectedDate.value = new Date(cell.year, cell.month, cell.day);
      if (!cell.currentMonth) {
        currentMonth.value = cell.month;
        currentYear.value = cell.year;
      }
    }

    function prevMonth() {
      if (currentMonth.value === 0) {
        currentMonth.value = 11;
        currentYear.value--;
      } else {
        currentMonth.value--;
      }
      emit('prev-month');
    }

    function nextMonth() {
      if (currentMonth.value === 11) {
        currentMonth.value = 0;
        currentYear.value++;
      } else {
        currentMonth.value++;
      }
      emit('next-month');
    }

    function goToToday() {
      const now = new Date();
      currentMonth.value = now.getMonth();
      currentYear.value = now.getFullYear();
      selectedDate.value = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      emit('today');
    }

    const selectedDateLabel = computed(() => {
      return selectedDate.value.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    });

    const selectedDateStr = computed(() => {
      const y = selectedDate.value.getFullYear();
      const m = String(selectedDate.value.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    });

    const selectedDateEvents = computed(() => {
      return props.events.filter((ev) => ev.date === selectedDateStr.value);
    });

    const upcomingEvents = computed(() => {
      const sel = selectedDateStr.value;
      return props.events.filter((ev) => ev.date > sel).slice(0, 3);
    });

    function getEventColor(event) {
      return props.colorMap[event.type] || props.defaultColor;
    }

    const badgeBgMap = {
      '#00A3E0': '#d9f1fa',
      '#FFC627': '#fef2d0'
    };

    function getBadgeBackground(event) {
      const color = getEventColor(event);
      return badgeBgMap[color] || '#fef2d0';
    }

    function badgeMonth(dateStr) {
      const d = parseLocalDate(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    }

    function badgeDay(dateStr) {
      const d = parseLocalDate(dateStr);
      return d.getDate();
    }

    return {
      dayHeaders,
      monthYearLabel,
      calendarWeeks,
      currentMonth,
      currentYear,
      selectedDate,
      selectedDateLabel,
      selectedDateEvents,
      upcomingEvents,
      prevMonth,
      nextMonth,
      goToToday,
      selectDate,
      isSelected,
      isToday,
      getDotColor,
      getEventColor,
      getBadgeBackground,
      badgeMonth,
      badgeDay
    };
  }
});
</script>

<style scoped>
.calendar-events {
  display: flex;
  gap: 24px;
}

/* ---------- LEFT: Mini Calendar ---------- */
.calendar-events__calendar {
  width: 315px;
  min-width: 315px;
}

.calendar-events__nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
}

.calendar-events__today-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #bfbfbf;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-family: Arial, sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: #191919;
  white-space: nowrap;
}

.calendar-events__today-btn:hover {
  background-color: #f5f5f5;
}

.calendar-events__nav-arrows {
  display: flex;
  align-items: center;
}

.calendar-events__nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;
}

.calendar-events__nav-btn:hover {
  background-color: #f5f5f5;
}

.calendar-events__nav-label {
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #000;
}

.calendar-events__day-headers {
  display: flex;
  padding: 12px 0;
}

.calendar-events__day-header {
  width: 45px;
  text-align: center;
  font-family: Arial, sans-serif;
  font-size: 12px;
  color: #747474;
}

.calendar-events__grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.calendar-events__week {
  display: flex;
  height: 40px;
}

.calendar-events__cell {
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  padding: 2px 0;
  position: relative;
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #000;
}

.calendar-events__cell:hover {
  background-color: #f5f5f5;
}

.calendar-events__cell--other-month {
  color: #bdbdbd;
}

.calendar-events__cell--selected {
  background-color: #e8e8e8;
}

.calendar-events__cell--today .calendar-events__cell-number {
  text-decoration: underline;
  text-decoration-color: #8c1d40;
  text-underline-offset: 3px;
  text-decoration-thickness: 2px;
  font-weight: bold;
}

.calendar-events__cell-number {
  line-height: 1;
}

.calendar-events__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  position: absolute;
  bottom: 3px;
}

/* ---------- RIGHT: Event Panel ---------- */
.calendar-events__panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.calendar-events__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.calendar-events__section-heading {
  font-family: Arial, sans-serif;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.48px;
  color: #000;
}

.calendar-events__empty {
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #747474;
}

.calendar-events__event-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #fafafa;
  border-radius: 8px;
  padding: 8px;
}

.calendar-events__event-card--flex {
  flex: 1;
}

.calendar-events__accent-bar {
  width: 4px;
  min-height: 32px;
  align-self: stretch;
  border-radius: 9999px;
}

.calendar-events__event-body {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.calendar-events__event-text {
  font-family: Arial, sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: #000;
  flex: 1;
}

/* Coming up next row */
.calendar-events__upcoming-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.calendar-events__date-badge {
  width: 48px;
  min-width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
}

.calendar-events__date-badge-month {
  font-family: Arial, sans-serif;
  font-weight: bold;
  font-size: 12px;
  letter-spacing: -0.36px;
  color: #191919;
  line-height: 1;
}

.calendar-events__date-badge-day {
  font-family: Arial, sans-serif;
  font-weight: bold;
  font-size: 16px;
  letter-spacing: -0.48px;
  color: #191919;
  line-height: 1.2;
}

/* Legend */
.calendar-events__legend {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.calendar-events__legend-info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.calendar-events__legend-prefix {
  font-family: Arial, sans-serif;
  font-size: 12px;
  line-height: 1;
  color: #747474;
}

.calendar-events__legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
}

.calendar-events__legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.calendar-events__legend-label {
  font-family: Arial, sans-serif;
  font-size: 12px;
  line-height: 1;
  color: #000;
}

/* ---------- Vertical (stacked) mode ---------- */
.calendar-events--vertical {
  flex-direction: column;
}

.calendar-events--vertical .calendar-events__calendar {
  width: 100%;
  min-width: unset;
}

/* ---------- Responsive ---------- */
@media (max-width: 768px) {
  .calendar-events {
    flex-direction: column;
  }

  .calendar-events__calendar {
    width: 100%;
    min-width: unset;
  }
}
</style>
